import React from 'react';
import { AppButton, AppMultiSelect, SearchTextField } from '@/components/atoms';
import { Search, Download } from '@/components/atoms/AppIcon';
import { RA2NDatePicker } from '@/components/organisms';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import type { TenantSiteTypeItem } from '@/services/tenants';
import { JA_JP } from '@/components/organisms/RA2NDatePicker/constants';
import { parseDate, parseFormat } from '@/components/organisms/RA2NDatePicker/datepickerLogic';

const dayFormat = parseFormat(JA_JP.format);

function ra2nDateToYYYYMMDD(value: string): string {
  if (!value?.trim()) return '';
  const d = parseDate(value, dayFormat);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  return `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

type ClinicsFilterPanelProps = {
  canUseSiteTypeFeatures: boolean;
  tenants: TenantSiteTypeItem[];
  siteTypes: string[];
  hasAllSelected: boolean;
  onChangeSiteTypes: (next: string[]) => void;

  searchClinicNumber: string;
  onChangeSearchClinicNumber: (next: string) => void;

  searchClinicName?: string;
  onChangeSearchClinicName?: (next: string) => void;

  availableDate: string;
  onChangeAvailableDate: (next: string) => void;

  onSearch: () => void;
  onCsv: () => void;
};

const ClinicsFilterPanel: React.FC<ClinicsFilterPanelProps> = ({
  canUseSiteTypeFeatures,
  tenants,
  siteTypes,
  hasAllSelected,
  onChangeSiteTypes,
  searchClinicNumber,
  onChangeSearchClinicNumber,
  searchClinicName,
  onChangeSearchClinicName,
  availableDate,
  onChangeAvailableDate,
  onSearch,
  onCsv,
}) => {
  const { colors } = useTheme();
  const tenantSiteTypes = tenants.map((t) => t.site_type).filter(Boolean) as string[];

  const hasClinicNameFilter = typeof onChangeSearchClinicName === 'function';

  const gridColsClass =
    canUseSiteTypeFeatures && hasClinicNameFilter
      ? 'grid-cols-5'
      : canUseSiteTypeFeatures || hasClinicNameFilter
      ? 'grid-cols-4'
      : 'grid-cols-3';
  return (
    <div className="bg-white rounded-md border p-4 mb-4">
      <div
        className={`grid gap-4 ${gridColsClass} pb-4 items-end`}
      >
        {canUseSiteTypeFeatures && (
          <div>
            <label htmlFor="site_types" className="mb-1 block text-sm text-gray-600">
              サイト区分
            </label>
            <AppMultiSelect
              id="site_types"
              placeholder="サイト区分"
              value={siteTypes}
              style={{ width: '100%' }}
              maxTagCount={hasAllSelected ? 0 : 'responsive'}
              maxTagPlaceholder={hasAllSelected ? () => 'すべて' : undefined}
              showSearch
              onInputKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
              options={[
                { label: 'すべて', value: 'all' },
                ...tenants.map((t) => ({ label: t.name, value: t.site_type })),
              ]}
              onChange={(values) => {
                const next = (values as string[]) || [];
                if (next.includes('all')) {
                  onChangeSiteTypes(tenantSiteTypes);
                  return;
                }
                onChangeSiteTypes(next.filter((v) => v !== 'all'));
              }}
            />
          </div>
        )}

        <div>
          <label htmlFor="search_clinic_number" className="mb-1 block text-sm text-gray-600">
            治療院ID
          </label>
          <SearchTextField
            id="search_clinic_number"
            value={searchClinicNumber}
            placeholder="治療院ID"
            onChange={(e) => onChangeSearchClinicNumber(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.stopPropagation();
                onSearch();
              }
            }}
          />
        </div>

        {typeof onChangeSearchClinicName === 'function' && (
          <div>
            <label htmlFor="search_clinic_name" className="mb-1 block text-sm text-gray-600">
              治療院名
            </label>
            <SearchTextField
              id="search_clinic_name"
              value={searchClinicName ?? ''}
              placeholder="治療院名"
              onChange={e => onChangeSearchClinicName(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.stopPropagation();
                  onSearch();
                }
              }}
            />
          </div>
        )}

        <div>
          <label htmlFor="available_date" className="mb-1 block text-sm text-gray-600">
            基準日
          </label>
          <RA2NDatePicker
            picker="day"
            className="w-full"
            value={availableDate || ''}
            onChange={v => onChangeAvailableDate(ra2nDateToYYYYMMDD(v))}
            placeholder="基準日"
            onEnter={onSearch}
          />
        </div>

        <div className="flex items-end gap-2">
          <AppButton
            onClick={onSearch}
            type="primary"
            icon={<Search />}
            style={{ backgroundColor: colors.secondary, borderColor: colors.secondary }}
            className="py-1 px-4 text-white h-auto btn-ra2-hover font-medium !h-[40px]"
          >
            検索
          </AppButton>
          <AppButton
            onClick={onCsv}
            type="primary"
            icon={<Download />}
            style={{ backgroundColor: colors.primary, borderColor: colors.primary }}
            className="py-1 px-4 text-white h-auto btn-ra2-hover font-medium !h-[40px]"
          >
            CSV
          </AppButton>
        </div>
      </div>
    </div>
  );
};

export default ClinicsFilterPanel;
